import { GRAVITY, LINES_PER_LEVEL, LOCK_DELAY } from './constants';
import { GameState, InputState, DASState, Piece, PieceType, createInitialState, getNextPiece } from './state';
import { spawnPiece, getCells, getWallKicks } from './piece';
import { isValidPosition, isOnGround, lockPiece, clearLines, isTopOut } from './board';
import { updateDAS } from './input';

export function createGame(): GameState {
  const state = createInitialState();
  spawnNextPiece(state);
  return state;
}

export function updateGame(state: GameState, input: InputState, das: DASState): void {
  if (state.gameOver) return;
  if (!state.currentPiece) return;

  // Handle horizontal movement with DAS
  const { moveLeft, moveRight } = updateDAS(das, input);

  if (moveLeft) {
    tryMove(state, -1, 0);
  }
  if (moveRight) {
    tryMove(state, 1, 0);
  }

  // Handle rotation
  if (input.rotateCW) {
    tryRotate(state, true);
  }
  if (input.rotateCCW) {
    tryRotate(state, false);
  }

  // Handle hold
  if (input.hold && state.canHold) {
    holdPiece(state);
  }

  // Handle hard drop
  if (input.hardDrop) {
    hardDrop(state);
    return;
  }

  // Handle soft drop and gravity
  const dropInterval = input.softDrop ? 2 : getDropInterval(state.level);

  state.dropTimer++;
  if (state.dropTimer >= dropInterval) {
    state.dropTimer = 0;

    if (isOnGround(state.board, state.currentPiece)) {
      // Start or continue lock delay
      if (!state.isLocking) {
        state.isLocking = true;
        state.lockTimer = 0;
      }
    } else {
      // Move down
      state.currentPiece.y++;
      state.isLocking = false;
      state.lockTimer = 0;
    }
  }

  // Handle lock delay
  if (state.isLocking) {
    state.lockTimer++;
    if (state.lockTimer >= LOCK_DELAY) {
      performLock(state);
    }
  }
}

function tryMove(state: GameState, dx: number, dy: number): boolean {
  if (!state.currentPiece) return false;

  const newPiece = {
    ...state.currentPiece,
    x: state.currentPiece.x + dx,
    y: state.currentPiece.y + dy,
  };

  if (isValidPosition(state.board, newPiece)) {
    state.currentPiece = newPiece;

    // Reset lock delay on successful move (but only on ground)
    if (state.isLocking && dy === 0) {
      state.lockTimer = 0;
    }

    return true;
  }

  return false;
}

function tryRotate(state: GameState, clockwise: boolean): boolean {
  if (!state.currentPiece) return false;

  const fromRotation = state.currentPiece.rotation;
  const toRotation = clockwise
    ? (fromRotation + 1) % 4
    : (fromRotation + 3) % 4;

  const kicks = getWallKicks(state.currentPiece.type, fromRotation, clockwise);

  for (const [dx, dy] of kicks) {
    const newPiece: Piece = {
      ...state.currentPiece,
      x: state.currentPiece.x + dx,
      y: state.currentPiece.y - dy, // Y is inverted in SRS kick data
      rotation: toRotation,
    };

    if (isValidPosition(state.board, newPiece)) {
      state.currentPiece = newPiece;

      // Reset lock delay on successful rotation
      if (state.isLocking) {
        state.lockTimer = 0;
      }

      return true;
    }
  }

  return false;
}

function hardDrop(state: GameState): void {
  if (!state.currentPiece) return;

  // Drop to bottom
  while (tryMove(state, 0, 1)) {
    state.score += 2; // Hard drop bonus
  }

  performLock(state);
}

function performLock(state: GameState): void {
  if (!state.currentPiece) return;

  // Check for top-out before locking
  if (isTopOut(state.board, state.currentPiece)) {
    lockPiece(state.board, state.currentPiece);
    state.gameOver = true;
    state.currentPiece = null;
    return;
  }

  lockPiece(state.board, state.currentPiece);

  // Clear lines
  const cleared = clearLines(state.board);
  if (cleared > 0) {
    state.lines += cleared;

    // Score based on lines cleared
    const lineScores = [0, 100, 300, 500, 800];
    state.score += lineScores[cleared] * state.level;

    // Level up
    const newLevel = Math.floor(state.lines / LINES_PER_LEVEL) + 1;
    if (newLevel > state.level) {
      state.level = newLevel;
    }
  }

  // Reset lock state
  state.isLocking = false;
  state.lockTimer = 0;
  state.canHold = true;

  // Spawn next piece
  spawnNextPiece(state);
}

function holdPiece(state: GameState): void {
  if (!state.currentPiece || !state.canHold) return;

  const currentType = state.currentPiece.type;

  if (state.heldPiece === null) {
    state.heldPiece = currentType;
    spawnNextPiece(state);
  } else {
    const held = state.heldPiece;
    state.heldPiece = currentType;
    state.currentPiece = spawnPiece(held);

    // Check if spawn position is valid
    if (!isValidPosition(state.board, state.currentPiece)) {
      state.gameOver = true;
    }
  }

  state.canHold = false;
  state.isLocking = false;
  state.lockTimer = 0;
  state.dropTimer = 0;
}

function spawnNextPiece(state: GameState): void {
  state.currentPiece = spawnPiece(state.nextPiece);
  state.nextPiece = getNextPiece(state);

  // Check if spawn position is valid
  if (!isValidPosition(state.board, state.currentPiece)) {
    state.gameOver = true;
  }
}

function getDropInterval(level: number): number {
  const idx = Math.min(level - 1, GRAVITY.length - 1);
  return GRAVITY[idx];
}
